import { Link } from "react-router-dom"
import MarketingLayout from "../../components/marketing/MarketingLayout"
import MarketingSeo from "../../components/marketing/MarketingSeo"
import { measurementStudioSalesMailto } from "../../components/marketing/salesLinks"
import { Button } from "../../components/ui"

const healthAreas = [
  ["Tagging health", "Coverage, identifiers, containers, and implementation drift"],
  ["Data quality", "Events, traffic signals, hostnames, referrals, and page data"],
  ["GA4 governance", "Property settings, access, channels, links, and conversions"],
  ["Account consistency", "One QA checklist applied across every client account"],
]

const workflow = [
  {
    number: "01",
    title: "Connect the measurement stack",
    description:
      "Organize websites, analytics properties, tag containers, and advertising accounts in one workspace.",
  },
  {
    number: "02",
    title: "Check health and quality",
    description:
      "Run focused checks for tagging coverage, configuration drift, and the data-quality issues that weaken reporting.",
  },
  {
    number: "03",
    title: "Keep every account aligned",
    description:
      "Compare client accounts against the same QA checklist and turn exceptions into a clear action list.",
  },
]

const faqs = [
  [
    "What is Measurement Studio?",
    "Measurement Studio is Jarbod’s industry-neutral workspace for monitoring tagging health, analytics data quality, and measurement standards across many client or business accounts.",
  ],
  [
    "What does it check?",
    "Measurement Studio covers website tagging, Google Tag Manager visibility, analytics identifiers, GA4 settings and events, traffic-quality signals, access, and paid-media account mappings.",
  ],
  [
    "Can we use our own standards?",
    "Measurement Studio Scale is coming soon. It is planned to let agencies define custom audit checks and configuration targets, then monitor those requirements across client accounts.",
  ],
  [
    "Do you have an automotive product?",
    "Yes. ASC TuneUp is Jarbod’s automotive-specific product for dealerships, dealer groups, and the agencies that support them.",
  ],
]

export default function MarketingHome() {
  return (
    <MarketingLayout>
      <MarketingSeo
        title="Analytics QA for Agencies | Measurement Studio by Jarbod"
        description="Catch broken tags, GA4 drift, and analytics data-quality issues across every client account with Measurement Studio by Jarbod."
      />
      <section className="marketing-hero">
        <div className="marketing-container marketing-hero__grid">
          <div>
            <p className="marketing-eyebrow">Measurement Studio by Jarbod</p>
            <h1 className="marketing-title">
              Catch broken tags and bad analytics data across <span className="marketing-title__accent">every client account.</span>
            </h1>
            <p className="marketing-lede">
              Measurement Studio gives agencies one place to run repeatable QA
              across websites, GTM, GA4, and paid media—so problems surface
              before they reach client reports.
            </p>
            <div className="marketing-actions">
              <Button as="a" href={measurementStudioSalesMailto} size="lg">
                Email sales
              </Button>
              <Button as={Link} to="/product" variant="outline" size="lg">
                See what it checks
              </Button>
            </div>
            <ul className="marketing-proof" aria-label="Measurement Studio details">
              <li>Built for agencies managing multiple client accounts</li>
              <li>One view across every account</li>
            </ul>
          </div>

          <div className="marketing-console" aria-label="Example Measurement Studio health summary">
            <div className="marketing-console__chrome" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <div className="marketing-console__body">
              <div className="marketing-console__topline">
                <span className="marketing-console__label">Tagging + data health</span>
                <span className="marketing-console__status">32 accounts checked</span>
              </div>
              <div className="marketing-console__metric">
                <div className="marketing-console__score">91<small>/100</small></div>
                <div className="marketing-console__bar" aria-hidden="true"><span /></div>
              </div>
              <div className="marketing-console__rows">
                <div className="marketing-console__row"><span>Tag coverage</span><strong>96%</strong></div>
                <div className="marketing-console__row"><span>Data-quality checks</span><strong>7 issues</strong></div>
                <div className="marketing-console__row"><span>GA4 configuration</span><strong>4 off standard</strong></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-foundation" aria-label="Measurement health areas">
        <div className="marketing-container marketing-foundation__grid">
          {healthAreas.map(([title, detail]) => (
            <div className="marketing-foundation__item" key={title}>
              <strong>{title}</strong>
              <span>{detail}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-section-heading--split">
            <div>
              <p className="marketing-eyebrow">Measurement operations</p>
              <h2>Replace spreadsheet audits with a repeatable QA process.</h2>
            </div>
            <p>
              Check every client consistently, surface configuration drift
              earlier, and give the team one place to see what needs attention.
            </p>
          </div>
          <div className="marketing-steps">
            {workflow.map((step) => (
              <article className="marketing-step" key={step.number}>
                <span>{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--surface">
        <div className="marketing-container marketing-split-feature">
          <div>
            <p className="marketing-eyebrow">Tagging health at a glance</p>
            <h2>See which client accounts need attention—without checking them one by one.</h2>
            <p className="marketing-copy">
              Compare tag coverage, analytics identifiers, property settings,
              events, and data-quality signals across clients. Focus the team
              on the accounts that actually need attention.
            </p>
            <Button as={Link} to="/product" variant="outline">
              See every health check
            </Button>
          </div>
          <div className="marketing-signal-board" aria-label="Example account portfolio">
            <div className="marketing-signal-board__header">
              <span>Account portfolio</span>
              <strong>32 monitored</strong>
            </div>
            {[
              ["Alpine Health", "On standard", "96"],
              ["Northstar Education", "Tag review", "84"],
              ["Fieldhouse Retail", "On standard", "93"],
              ["Harbor Financial", "Data issue", "88"],
            ].map(([name, status, score]) => (
              <div className="marketing-signal-board__row" key={name}>
                <span className="marketing-signal-board__dot" aria-hidden="true" />
                <strong>{name}</strong>
                <span>{status}</span>
                <b>{score}</b>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-container marketing-pricing-preview marketing-product-callout">
          <div>
            <p className="marketing-eyebrow">Built for your operating model</p>
            <h2>Start with repeatable QA. Shape the checks around your agency.</h2>
            <p className="marketing-copy">
              Measurement Studio pricing reflects the number of client accounts,
              the checks your agency needs, and how often those checks should run.
            </p>
          </div>
          <div className="marketing-product-callout__label">
            <span>Jarbod</span>
            <strong>Measurement Studio</strong>
            <p>Tagging health · Analytics QA · Client accounts</p>
          </div>
          <Button as={Link} to="/pricing" variant="secondary">
            View program options
          </Button>
        </div>
      </section>

      <section className="marketing-section marketing-section--surface">
        <div className="marketing-container marketing-split-feature">
          <div>
            <p className="marketing-eyebrow">For automotive agencies and dealer groups</p>
            <h2>Work with dealerships? Meet ASC TuneUp.</h2>
            <p className="marketing-copy">
              ASC TuneUp is Jarbod’s automotive-specific experience, with
              dealership accounts, rooftop portfolios, automotive reporting
              workflows, plus a coming-soon Scale roadmap.
            </p>
            <Button as={Link} to="/asc-tuneup">
              Explore ASC TuneUp
            </Button>
          </div>
          <div className="marketing-vertical-card" aria-label="ASC TuneUp automotive product">
            <span>Automotive product</span>
            <strong>ASC TuneUp</strong>
            <p>Tagging and analytics QA for dealerships, dealer groups, and automotive agencies.</p>
            <div>
              <b>Core audits</b>
              <b>Scale coming soon</b>
              <b>Scale roadmap</b>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--compact">
        <div className="marketing-container marketing-faq-layout">
          <div>
            <p className="marketing-eyebrow">Frequently asked</p>
            <h2>Agency analytics QA, made repeatable.</h2>
          </div>
          <div className="marketing-faq-list">
            {faqs.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--cta">
        <div className="marketing-container marketing-cta-panel">
          <div>
            <p className="marketing-eyebrow">Build a stronger QA process</p>
            <h2>Tell us how many client accounts you manage.</h2>
          </div>
          <div className="marketing-actions">
            <Button as="a" href={measurementStudioSalesMailto} size="lg">Email sales</Button>
            <Button as={Link} to="/product" variant="outline" size="lg">
              Explore the platform
            </Button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  )
}

